/**
 * assets/ad-compare.js
 * Compare tray + compare page controller.
 * No framework dependencies.
 *
 * Storage: localStorage key 'adCompareItems' — array of product spec objects, max 3.
 * Events:  compare:updated — dispatched whenever the store changes.
 */

(function () {
  const KEY = 'adCompareItems';
  const MAX = 3;

  // ── Storage helpers ──────────────────────────────────────────────────────────
  const adCompare = {
    getItems() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
      catch { return []; }
    },
    save(items) {
      localStorage.setItem(KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('compare:updated'));
    },
    add(data) {
      const items = this.getItems();
      if (items.length >= MAX || items.some(i => i.handle === data.handle)) return false;
      items.push(data);
      this.save(items);
      return true;
    },
    remove(handle) {
      this.save(this.getItems().filter(i => i.handle !== handle));
    },
    clear() {
      localStorage.removeItem(KEY);
      document.dispatchEvent(new CustomEvent('compare:updated'));
    },
    has(handle) {
      return this.getItems().some(i => i.handle === handle);
    },
  };

  // ── Compare tray (rendered in compare-tray.liquid) ───────────────────────────
  function renderTray() {
    const tray = document.querySelector('[data-compare-tray]');
    if (!tray) return;

    const items = adCompare.getItems();
    tray.setAttribute('aria-hidden', items.length === 0 ? 'true' : 'false');

    const slots = tray.querySelector('[data-compare-slots]');
    if (slots) {
      slots.innerHTML = items.map(item => `
        <div class="ad-compare-tray__slot">
          <span>${escHtml(item.title)}</span>
          <button type="button" data-compare-remove="${escHtml(item.handle)}"
            aria-label="Remove ${escHtml(item.title)} from compare">✕</button>
        </div>
      `).join('');
    }

    // Sync any Add-to-Compare button on the current page
    syncAddBtn();
  }

  function syncAddBtn() {
    const btn = document.querySelector('[data-compare-add]');
    if (btn) {
      const inList = adCompare.has(btn.dataset.compareAdd);
      btn.textContent = inList ? 'Remove from Compare' : 'Add to Compare';
      btn.classList.toggle('is-added', inList);
    }
    document.querySelectorAll('[data-compare-card][data-compare-handle]').forEach(syncCardBtn);
  }

  function syncCardBtn(btn) {
    const handle = btn.dataset.compareHandle;
    if (!handle) return;
    const inList = adCompare.has(handle);
    btn.textContent = inList ? 'Added' : 'Compare';
    btn.classList.toggle('is-added', inList);
    btn.disabled = !inList && adCompare.getItems().length >= MAX;
  }

  function initTray() {
    const tray = document.querySelector('[data-compare-tray]');
    if (!tray) return;

    tray.addEventListener('click', e => {
      const removeBtn = e.target.closest('[data-compare-remove]');
      const clearBtn = e.target.closest('[data-compare-clear]');
      if (removeBtn) { adCompare.remove(removeBtn.dataset.compareRemove); renderTray(); }
      if (clearBtn)  { adCompare.clear(); renderTray(); }
    });

    document.addEventListener('compare:updated', renderTray);
    renderTray();
  }

  // ── PDP "Add to Compare" button ──────────────────────────────────────────────
  function initPdpButton() {
    const btn = document.querySelector('[data-compare-add]');
    const dataEl = document.getElementById('ad-product-data');
    if (!btn || !dataEl) return;

    let productData;
    try { productData = JSON.parse(dataEl.textContent); }
    catch { return; }

    syncAddBtn();

    btn.addEventListener('click', () => {
      if (adCompare.has(productData.handle)) {
        adCompare.remove(productData.handle);
      } else {
        const added = adCompare.add(productData);
        if (!added) {
          const prev = btn.textContent;
          btn.textContent = 'Max 3 items';
          setTimeout(() => { btn.textContent = prev; }, 2000);
          return;
        }
      }
      renderTray();
    });
  }

  // ── Collection card "Compare" buttons ────────────────────────────────────────
  function initCardButtons() {
    document.querySelectorAll('[data-compare-card]').forEach(btn => {
      const dataEl = document.getElementById('ad-pdata-' + btn.dataset.compareCard);
      if (!dataEl) return;
      let productData;
      try { productData = JSON.parse(dataEl.textContent); }
      catch { return; }

      btn.dataset.compareHandle = productData.handle;
      syncCardBtn(btn);

      btn.addEventListener('click', () => {
        if (adCompare.has(productData.handle)) {
          adCompare.remove(productData.handle);
        } else {
          if (adCompare.getItems().length >= MAX) {
            const prev = btn.textContent;
            btn.textContent = 'Max 3';
            setTimeout(() => { syncCardBtn(btn); }, 1800);
            return;
          }
          adCompare.add(productData);
        }
        renderTray();
      });
    });
  }

  // ── Compare page (/pages/compare) ───────────────────────────────────────────
  function initComparePage() {
    const page = document.querySelector('[data-compare-page]');
    if (!page) return;

    render();

    function render() {
      const items = adCompare.getItems();

      if (items.length === 0) {
        page.innerHTML = `
          <div class="ad-compare-empty">
            <p class="ad-compare-empty__msg">No diamonds selected for comparison.</p>
            <a href="/collections/all" class="ad-btn ad-btn--primary">Browse Diamonds</a>
          </div>`;
        return;
      }

      const specs = [
        { key: 'shape',       label: 'Shape' },
        { key: 'carat',       label: 'Carat' },
        { key: 'color',       label: 'Color' },
        { key: 'clarity',     label: 'Clarity' },
        { key: 'cut',         label: 'Cut' },
        { key: 'polish',      label: 'Polish' },
        { key: 'cert_type',   label: 'Certificate' },
        { key: 'growth_type', label: 'Growth Type' },
      ];

      const colCount = items.length;

      page.innerHTML = `
        <div class="ad-compare-table" style="--cols:${colCount}">
          <!-- Header row: product cards -->
          <div class="ad-compare-table__label-cell"></div>
          ${items.map(item => `
            <div class="ad-compare-col-header">
              ${item.image ? `<img src="${escHtml(item.image)}" alt="${escHtml(item.title)}" width="280" height="280" class="ad-compare-col-header__img">` : ''}
              <h2 class="ad-compare-col-header__title">${escHtml(item.title)}</h2>
              <p class="ad-compare-col-header__price">${escHtml(item.price)}</p>
              <a href="${escHtml(item.url)}" class="ad-btn ad-btn--primary ad-compare-col-header__cta">View Diamond</a>
              <button type="button" class="ad-compare-col-header__remove" data-compare-remove="${escHtml(item.handle)}">Remove</button>
            </div>
          `).join('')}

          <!-- Spec rows -->
          ${specs.map(spec => `
            <div class="ad-compare-table__label-cell ad-compare-spec-label">${escHtml(spec.label)}</div>
            ${items.map(item => `
              <div class="ad-compare-spec-value">${escHtml(String(item[spec.key] || '—'))}</div>
            `).join('')}
          `).join('')}

          <!-- Certificate links row -->
          <div class="ad-compare-table__label-cell ad-compare-spec-label">Certificate</div>
          ${items.map(item => `
            <div class="ad-compare-spec-value">
              ${item.cert_url
                ? `<a href="${escHtml(item.cert_url)}" target="_blank" rel="noopener noreferrer" class="ad-compare-cert-link">View PDF</a>`
                : '—'}
            </div>
          `).join('')}
        </div>

        <div class="ad-compare-page-actions">
          <button type="button" class="ad-btn ad-btn--ghost" data-compare-clear>Clear All</button>
          <a href="/collections/all" class="ad-btn ad-btn--ghost">Browse More</a>
        </div>
      `;

      page.querySelectorAll('[data-compare-remove]').forEach(btn => {
        btn.addEventListener('click', () => { adCompare.remove(btn.dataset.compareRemove); render(); });
      });
      page.querySelector('[data-compare-clear]')?.addEventListener('click', () => { adCompare.clear(); render(); });
    }
  }

  // ── Utility ──────────────────────────────────────────────────────────────────
  function escHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  // ── Boot ─────────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    initTray();
    initPdpButton();
    initCardButtons();
    initComparePage();
  });
})();
